import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Hourglass, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";
import { AttachmentManager } from "../components/AttachmentManager";

export default function Suspense() {
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [persons, setPersons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openSettle, setOpenSettle] = useState(false);
  const [openOut, setOpenOut] = useState(false);
  const [outForm, setOutForm] = useState({ person_id: "", amount: "", category_id: "", particulars: "", remarks: "" });
  const [attach, setAttach] = useState({ open: false, txnId: null, docNo: null });
  const [form, setForm] = useState({ person_id: "", settlement_type: "bill", amount: "", linked_doc_no: "", particulars: "", remarks: "" });
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(0);

  const load = async () => {
    try {
      const [s, t, p, c] = await Promise.all([
        api.get("/suspense/summary"), api.get("/suspense/transactions"), api.get("/persons"), api.get("/categories"),
      ]);
      setSummary(s.data); setTxns(t.data); setPersons(p.data.filter(x => x.active));
      setCategories(c.data.filter(x => x.active));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const submitOut = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const r = await api.post("/cv", {
        amount: parseFloat(outForm.amount), payment_type: "suspense_advance",
        person_id: outForm.person_id, category_id: outForm.category_id || null,
        particulars: outForm.particulars, remarks: outForm.remarks || null,
      });
      toast.success(`Suspense out recorded · ${r.data.doc_no}`); setOpenOut(false);
      setOutForm({ person_id: "", amount: "", category_id: "", particulars: "", remarks: "" });
      await load();
      setAttach({ open: true, txnId: r.data.id, docNo: r.data.doc_no });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  useEffect(() => {
    if (!form.person_id || !summary) return setPending(0);
    const r = summary.persons.find(x => x.person_id === form.person_id);
    setPending(r ? r.pending : 0);
  }, [form.person_id, summary]);

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const r = await api.post("/suspense/settle", {
        person_id: form.person_id, settlement_type: form.settlement_type,
        amount: parseFloat(form.amount), particulars: form.particulars,
        linked_doc_no: form.linked_doc_no || null, remarks: form.remarks || null,
      });
      toast.success(`Settlement recorded · ${r.data.doc_no}`); setOpenSettle(false);
      setForm({ person_id: "", settlement_type: "bill", amount: "", linked_doc_no: "", particulars: "", remarks: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  if (!summary) return <div className="font-mono text-sm text-muted">Loading…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Suspense</div>
          <h1 className="font-heading text-4xl font-bold mt-1">Pending Suspense</h1>
          <p className="text-sm text-muted mt-1">Cash with persons awaiting bill submission or return.</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setOpenOut(true)} data-testid="create-suspense-out-btn" className="bg-cashout hover:bg-cashout/90 text-white">
            <ArrowDownRight size={16} className="mr-1.5" /> Create Suspense Out
          </Button>
          <Button onClick={() => setOpenSettle(true)} data-testid="settle-suspense-btn" className="bg-ink hover:bg-ink-soft text-white">
            <Hourglass size={16} className="mr-1.5" /> Settle Suspense
          </Button>
        </div>
      </div>

      <Card className="p-6 border border-line bg-paper-card overflow-hidden">
        <div className="label-eyebrow">Total Pending</div>
        {/* Never truncate, never wrap mid-number — whitespace-nowrap keeps it on one line, and the type
            scales down on narrow screens so the whole figure still fits (same rule as CashBook's cards). */}
        <div className="mt-2 font-mono tabular-nums whitespace-nowrap text-2xl sm:text-3xl lg:text-4xl font-bold text-warn" data-testid="total-pending-suspense">{inr(summary.total_pending)}</div>
      </Card>

      <Card className="overflow-hidden border border-line">
        <div className="px-6 py-4 border-b border-line"><div className="label-eyebrow">Person-wise Pending</div></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-paper-alt border-b border-line">
              <th className="text-left px-4 py-3 label-eyebrow">Person</th>
              <th className="text-right px-4 py-3 label-eyebrow">Advanced</th>
              <th className="text-right px-4 py-3 label-eyebrow">Settled</th>
              <th className="text-right px-4 py-3 label-eyebrow">Pending</th>
            </tr></thead>
            <tbody data-testid="person-suspense-rows">
              {summary.persons.map(p => (
                <tr key={p.person_id} className="border-b border-line">
                  <td className="px-4 py-3 font-medium">{p.person_name}</td>
                  <td className="px-4 py-3 text-right font-mono text-cashout">{inr(p.advanced)}</td>
                  <td className="px-4 py-3 text-right font-mono text-cashin">{inr(p.settled)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{inr(p.pending)}</td>
                </tr>
              ))}
              {summary.persons.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-muted">No suspense activity yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="overflow-hidden border border-line">
        <div className="px-6 py-4 border-b border-line"><div className="label-eyebrow">Recent Suspense Movements</div></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-paper-alt border-b border-line">
              <th className="text-left px-4 py-3 label-eyebrow">Date</th>
              <th className="text-left px-4 py-3 label-eyebrow">Type</th>
              <th className="text-left px-4 py-3 label-eyebrow">Doc</th>
              <th className="text-left px-4 py-3 label-eyebrow">Person</th>
              <th className="text-left px-4 py-3 label-eyebrow">Particulars</th>
              <th className="text-right px-4 py-3 label-eyebrow">Dr</th>
              <th className="text-right px-4 py-3 label-eyebrow">Cr</th>
            </tr></thead>
            <tbody>
              {txns.map(t => (
                <tr key={t.id} className="border-b border-line">
                  <td className="px-4 py-3 font-mono text-xs">{t.date}</td>
                  <td className="px-4 py-3 text-xs">{t.entry_label}</td>
                  <td className="px-4 py-3 font-mono text-xs">{t.doc_no || "—"}</td>
                  <td className="px-4 py-3">{t.person_name}</td>
                  <td className="px-4 py-3 text-muted">{t.particulars}</td>
                  <td className="px-4 py-3 text-right font-mono text-cashout">{t.sus_dr ? inr(t.sus_dr) : ""}</td>
                  <td className="px-4 py-3 text-right font-mono text-cashin">{t.sus_cr ? inr(t.sus_cr) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={openSettle} onOpenChange={setOpenSettle}>
        <DialogContent>
          <DialogHeader><DialogTitle>Settle Suspense</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label className="label-eyebrow">Person</Label>
              <Select value={form.person_id} onValueChange={(v) => setForm({ ...form, person_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="settle-person"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{summary.persons.map(p => <SelectItem key={p.person_id} value={p.person_id}>{p.person_name} (pending {inr(p.pending)})</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="label-eyebrow">Pending Suspense</Label>
              <div className="mt-1.5 font-mono font-semibold">{inr(pending)}</div></div>
            <div><Label className="label-eyebrow">Settlement Type</Label>
              <Select value={form.settlement_type} onValueChange={(v) => setForm({ ...form, settlement_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="settle-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bill">Bill Settled (expense)</SelectItem>
                  <SelectItem value="cash">Cash Returned (back into box)</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Amount</Label>
                <Input type="number" min="0.01" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="settle-amount" className="mt-1.5 font-mono" /></div>
              <div><Label className="label-eyebrow">Linked SA (optional)</Label>
                <Input value={form.linked_doc_no} onChange={(e) => setForm({ ...form, linked_doc_no: e.target.value })}
                  placeholder="SA 003" className="mt-1.5 font-mono" /></div>
            </div>
            <div><Label className="label-eyebrow">Particulars</Label>
              <Input required value={form.particulars} onChange={(e) => setForm({ ...form, particulars: e.target.value })} data-testid="settle-particulars" className="mt-1.5" /></div>
            <div><Label className="label-eyebrow">Remarks</Label>
              <Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1.5" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenSettle(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="settle-submit-btn" className="bg-ink text-white">Settle</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={openOut} onOpenChange={setOpenOut}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowDownRight size={18} /> Create Suspense Out</DialogTitle>
            <DialogDescription>Cash advanced to a person — an SA number is auto-generated and pending suspense is created. You can attach the voucher photo after saving.</DialogDescription></DialogHeader>
          <form onSubmit={submitOut} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Date</Label><div className="mt-1.5 font-mono text-sm">{summary.date || "today"}</div></div>
              <div><Label className="label-eyebrow">SA No.</Label><div className="mt-1.5 font-mono text-sm text-muted">auto-generated</div></div>
            </div>
            <div><Label className="label-eyebrow">Person (required)</Label>
              <Select value={outForm.person_id} onValueChange={(v) => setOutForm({ ...outForm, person_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="out-person"><SelectValue placeholder="Select person" /></SelectTrigger>
                <SelectContent>{persons.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Amount (₹)</Label>
                <Input type="number" min="0.01" step="0.01" required value={outForm.amount} onChange={(e) => setOutForm({ ...outForm, amount: e.target.value })}
                  data-testid="out-amount" className="mt-1.5 font-mono" /></div>
              <div><Label className="label-eyebrow">Category</Label>
                <Select value={outForm.category_id} onValueChange={(v) => setOutForm({ ...outForm, category_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="out-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.filter(c => c.type === "out" || c.type === "both").map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="label-eyebrow">Particulars</Label>
              <Input required value={outForm.particulars} onChange={(e) => setOutForm({ ...outForm, particulars: e.target.value })} data-testid="out-particulars" className="mt-1.5" /></div>
            <div><Label className="label-eyebrow">Remarks</Label>
              <Textarea rows={2} value={outForm.remarks} onChange={(e) => setOutForm({ ...outForm, remarks: e.target.value })} className="mt-1.5" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenOut(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="out-submit-btn" className="bg-cashout hover:bg-cashout/90 text-white">Save Suspense Out</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AttachmentManager
        open={attach.open}
        txnId={attach.txnId}
        docNo={attach.docNo}
        onOpenChange={(o) => setAttach((s) => ({ ...s, open: o }))}
        onChanged={load}
      />
    </div>
  );
}
